/**
 * ThemeProvider — light/dark/system appearance.
 *
 * Single source of truth for the applied theme:
 *   • Preference ('light' | 'dark' | 'system') is held in state, mirrored to
 *     localStorage ('vari-crew.theme') for an instant, flash-free apply on the
 *     next load — the inline script in index.html reads the same key pre-paint.
 *   • The resolved theme (light | dark) toggles the `.dark` class on <html>;
 *     every color utility reads CSS vars that index.css re-points under .dark.
 *   • The DB is the cross-device source of truth: when GET /me reports a saved
 *     preference we adopt it (once per distinct value, so a local toggle isn't
 *     clobbered before the /me cache catches up).
 *
 * Persisting a *change* to the DB is the toggle's job (see api/theme.ts); this
 * provider only reflects state and never writes to the server itself.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Toaster } from 'sonner';
import type { ThemePreference } from '@vari-crew/schemas';
import { useCurrentUser } from '@/auth/useCurrentUser';

type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'vari-crew.theme';
// Mobile browser chrome (<meta name="theme-color">): brand teal in light, the
// dark-mode --color-bg in dark. Kept in sync as the resolved theme changes.
const META_LIGHT = '#0e6b6b';
const META_DARK = '#11151b';

type ThemeContextValue = {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return prefersDark() ? 'dark' : 'light';
  return pref;
}

function apply(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? META_DARK : META_LIGHT);
}

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage blocked */
  }
  return 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(readStored);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(preference));

  // Apply on mount and whenever the preference changes.
  useEffect(() => {
    const r = resolve(preference);
    setResolved(r);
    apply(r);
  }, [preference]);

  // While following the system, react to OS light/dark changes live.
  useEffect(() => {
    if (preference !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const r: ResolvedTheme = mql.matches ? 'dark' : 'light';
      setResolved(r);
      apply(r);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPref(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage blocked — in-memory only for this session */
    }
  }, []);

  // Adopt the server's saved preference (cross-device truth). Apply each
  // distinct value once so a just-made local choice isn't reverted while the
  // /me cache is briefly stale.
  const { data: me } = useCurrentUser();
  const lastServer = useRef<ThemePreference | null>(null);
  useEffect(() => {
    const serverPref = me?.theme_preference;
    if (!serverPref || serverPref === lastServer.current) return;
    lastServer.current = serverPref;
    setPreference(serverPref);
  }, [me?.theme_preference, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}

/** Sonner toaster that follows the active theme (dark toasts in dark mode). */
export function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster position="top-right" richColors theme={resolved} />;
}
